import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentMethod = searchParams.get("method");
    const paypalToken = searchParams.get("token");

    // PayPal payment flow
    if (paymentMethod === "paypal" && paypalToken) {
      const capturePayPalPayment = async () => {
        try {
          const response = await axios.post(`${API}/paypal/capture-order/${paypalToken}`);
          if (response.data.status === "success") {
            await refreshUser();
            setStatus("success");
          } else {
            setStatus("pending");
          }
        } catch (error) {
          console.error("PayPal capture failed:", error);
          setStatus("failed");
        }
      };
      capturePayPalPayment();
      return;
    }

    // Stripe payment flow
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          await refreshUser();
          setStatus("success");
          return;
        }
        
        if (response.data.status === "expired" || attempts >= maxAttempts) {
          setStatus("failed");
          return;
        }

        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      } catch (error) {
        console.error("Payment status check failed:", error);
        if (attempts >= maxAttempts) {
          setStatus("failed");
        } else {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams, refreshUser, attempts]);

  const getDashboardLink = () => {
    if (user?.user_type === "candidate") return "/dashboard/candidate";
    if (user?.user_type === "service_provider") return "/dashboard/provider";
    return "/";
  };

  const getContentLink = () => {
    if (user?.user_type === "candidate") return "/jobs";
    if (user?.user_type === "service_provider") return "/dashboard/provider";
    return "/";
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="payment-success-page">
      <Navbar />
      
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          {status === "loading" && (
            <div className="animate-fadeIn">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#004E64] animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3">Verifica del pagamento...</h1>
              <p className="text-slate-600">Stiamo verificando il tuo pagamento. Attendi qualche secondo.</p>
            </div>
          )}

          {status === "success" && (
            <div className="animate-fadeIn">
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                <h1 className="text-3xl font-bold text-emerald-700 mb-1">PAGAMENTO RIUSCITO</h1>
                <p className="text-emerald-600 font-medium">Il tuo servizio è attivo!</p>
              </div>
              <p className="text-slate-600 mb-8">
                {user?.user_type === "candidate" 
                  ? "Grazie per il tuo abbonamento. Ora puoi candidarti a tutti gli annunci e contattare le aziende!"
                  : "Grazie per il tuo abbonamento. Ora puoi pubblicare i tuoi servizi e ricevere clienti!"
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={getContentLink()}>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6">
                    {user?.user_type === "candidate" ? "Cerca annunci" : "Pubblica servizio"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to={getDashboardLink()}>
                  <Button variant="outline" className="border-slate-300">Vai alla dashboard</Button>
                </Link>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="animate-fadeIn">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3">Pagamento non completato</h1>
              <p className="text-slate-600 mb-8">Si è verificato un problema. Riprova o contattaci.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/subscription">
                  <Button className="bg-[#DA291C] hover:bg-[#B91C1C] text-white">Riprova</Button>
                </Link>
                <Link to={getDashboardLink()}>
                  <Button variant="outline">Torna alla dashboard</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
